import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';

/**
 * Hook to manage profile completion status and navigation
 */
export const useProfileCompletion = () => {
 const { user, updateUser } = useAuth();
 const navigate = useNavigate();
 const location = useLocation();
 const [completionStatus, setCompletionStatus] = useState(null);
 const [loading, setLoading] = useState(true);
 const [checkingProfile, setCheckingProfile] = useState(false);

 // Routes that don't require complete profile
 const exemptRoutes = [
 '/login',
 '/register',
 '/complete-profile',
 '/logout',
 '/profile',
 '/notification-settings',
 '/api/profile',
 '/api/auth'
 ];

 const checkProfileCompletion = async () => {
 try {
 setCheckingProfile(true);
 console.log('[PROFILE HOOK] Checking profile completion...');

 const response = await API.get('/profile/profile-completion');
 const status = response.data.profileCompletion;

 console.log('[PROFILE HOOK] Profile completion status:', status);

 setCompletionStatus(status);

 // Update user in auth context with completion status
 if (user) {
 const updatedUser = {
 ...user,
 profileCompletion: status
 };
 console.log('[PROFILE HOOK] Updating user with completion status:', updatedUser);
 updateUser(updatedUser);
 }

 return status;
 } catch (error) {
 console.error('Error checking profile completion:', error);
 
 // If API call fails, assume profile is incomplete for safety
 const fallbackStatus = {
 isComplete: false,
 completionPercentage: 0,
 completedFields: [],
 missingFields: ['fullName', 'phone', 'department', 'company'],
 requiredFields: ['fullName', 'phone', 'department', 'company']
 };
 
 setCompletionStatus(fallbackStatus);
 return fallbackStatus;
 } finally {
 setCheckingProfile(false);
 setLoading(false);
 }
 };

 const isRouteExempt = (pathname) => {
 return exemptRoutes.some(route => 
 pathname === route || pathname.startsWith(route + '/')
 );
 };

 const requireCompleteProfile = () => {
 const currentPath = location.pathname;
 
 // Don't redirect if already on exempt route
 if (isRouteExempt(currentPath)) {
 return true;
 }
 
 // Don't redirect if profile is complete
 if (completionStatus?.isComplete) {
 return true;
 }
 
 // Redirect to profile completion page
 console.log('Profile incomplete, redirecting to completion page');
 toast.warning('Vui lòng hoàn thành thông tin cá nhân để tiếp tục sử dụng hệ thống');
 navigate('/complete-profile', { 
 replace: true,
 state: { from: currentPath }
 });
 
 return false;
 };

 const handleProfileComplete = (updatedUser) => {
 console.log('Profile completed successfully');
 setCompletionStatus(updatedUser.profileCompletion);
 
 // Update user in auth context
 updateUser(updatedUser);
 
 // Navigate back to intended page or dashboard
 const intendedPath = location.state?.from || '/';
 navigate(intendedPath, { replace: true });
 
 toast.success('Thông tin cá nhân đã được hoàn thành! Chào mừng bạn đến với hệ thống.');
 };

 // Check profile completion on mount and user change
 useEffect(() => {
 if (user && user.id) {
 checkProfileCompletion();
 } else {
 setLoading(false);
 }
 }, [user?.id]);

 // Auto-redirect if profile is incomplete and not on exempt route
 useEffect(() => {
 if (!loading && !checkingProfile && user && completionStatus) {
 const currentPath = location.pathname;

 console.log('[PROFILE HOOK] Navigation check:', {
 currentPath,
 isComplete: completionStatus.isComplete,
 isExempt: isRouteExempt(currentPath),
 loading,
 checkingProfile
 });

 // Only redirect to complete-profile if profile is incomplete AND not already on complete-profile page
 if (!completionStatus.isComplete && !isRouteExempt(currentPath) && currentPath !== '/complete-profile') {
 console.log('[PROFILE HOOK] Profile incomplete, redirecting to complete-profile');
 requireCompleteProfile();
 }

 // DON'T auto-redirect from complete-profile when complete - let the component handle it
 // This prevents the infinite loop
 // if (completionStatus.isComplete && currentPath === '/complete-profile') {
 // console.log('[PROFILE HOOK] Profile complete on complete-profile page, redirecting to home...');
 // const intendedPath = location.state?.from || '/';
 // console.log('[PROFILE HOOK] Intended path:', intendedPath);
 // navigate(intendedPath, { replace: true });
 // }
 }
 }, [loading, checkingProfile, completionStatus, location.pathname, user, navigate, location.state, isRouteExempt, requireCompleteProfile]);

 return {
 completionStatus,
 loading,
 checkingProfile,
 isComplete: completionStatus?.isComplete || false,
 completionPercentage: completionStatus?.completionPercentage || 0,
 missingFields: completionStatus?.missingFields || [],
 completedFields: completionStatus?.completedFields || [],
 requiredFields: completionStatus?.requiredFields || [],
 checkProfileCompletion,
 requireCompleteProfile,
 handleProfileComplete,
 isRouteExempt
 };
};

/**
 * Hook for API calls that need profile completion check
 */
export const useProfileGuardedAPI = () => {
 const { completionStatus } = useProfileCompletion();

 const guardedAPICall = async (apiCall, options = {}) => {
 const { skipProfileCheck = false, showWarning = true } = options;

 // Skip check if explicitly requested
 if (skipProfileCheck) {
 return await apiCall();
 }

 // Check if profile is complete
 if (!completionStatus?.isComplete) {
 if (showWarning) {
 toast.warning('Vui lòng hoàn thành thông tin cá nhân trước khi thực hiện thao tác này');
 }
 throw new Error('Profile incomplete');
 }

 // Profile is complete, proceed with API call
 return await apiCall();
 };

 return {
 guardedAPICall,
 isProfileComplete: completionStatus?.isComplete || false
 };
};

export default useProfileCompletion;
